import { createContext, useContext, useState, FC, ReactNode, useCallback, useEffect } from 'react';

type SidebarState = {
  collapsed: boolean;
  setCollapsed: (value: boolean) => void;
  toggle: () => void;
  requestedOpenMenu: string | null;
  openMenuOnExpand: (title: string) => void;
};

const SidebarContext = createContext<SidebarState>({
  collapsed: false,
  setCollapsed: () => {},
  toggle: () => {},
  requestedOpenMenu: null,
  openMenuOnExpand: () => {},
});

export function useSidebar() {
  return useContext(SidebarContext);
}

type ProviderProps = {
  children: ReactNode;
};

export const SidebarProvider: FC<ProviderProps> = ({ children }) => {
  const [collapsed, setCollapsedState] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 1024;
  });
  const [requestedOpenMenu, setRequestedOpenMenu] = useState<string | null>(null);

  const setCollapsed = useCallback((value: boolean) => {
    setRequestedOpenMenu(null);
    setCollapsedState(value);
  }, []);

  const toggle = useCallback(() => {
    setRequestedOpenMenu(null);
    setCollapsedState(prev => !prev);
  }, []);

  const openMenuOnExpand = useCallback((title: string) => {
    setRequestedOpenMenu(title);
    setCollapsedState(false);
  }, []);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return;
    }

    const mobileQuery = window.matchMedia('(max-width: 1023px)');

    const handleBreakpointChange = (event: MediaQueryListEvent | MediaQueryList) => {
      if (event.matches) {
        setCollapsed(true);
      }
    };

    handleBreakpointChange(mobileQuery);

    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', handleBreakpointChange);
      return () => mobileQuery.removeEventListener('change', handleBreakpointChange);
    }

    mobileQuery.addListener(handleBreakpointChange);
    return () => mobileQuery.removeListener(handleBreakpointChange);
  }, []);

  return (
    <SidebarContext.Provider value={{ collapsed, setCollapsed, toggle, requestedOpenMenu, openMenuOnExpand }}>
      {children}
    </SidebarContext.Provider>
  );
};
